// src/components/JobEditModal.tsx
import React from 'react';
import { Edit3, X } from 'lucide-react';

interface JobEditModalProps {
    editingJob: any;
    setEditingJob: (job: any | null) => void;
    saveEditedJob: (e: React.FormEvent) => void;
    stringers: any[];
}

export default function JobEditModal({ 
    editingJob, 
    setEditingJob, 
    saveEditedJob, 
    stringers 
}: JobEditModalProps) {
    
    // Extra veiligheidscheck: render niets als er geen job is om te bewerken
    if (!editingJob) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] backdrop-blur-sm p-4 no-print overflow-y-auto">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-auto">
                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                    <h3 className="font-bold text-lg text-slate-800 dark:text-white flex items-center">
                        <Edit3 className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400" /> Opdracht Bewerken
                    </h3>
                    <button type="button" onClick={() => setEditingJob(null)}>
                        <X className="w-5 h-5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" />
                    </button>
                </div>
                <form onSubmit={saveEditedJob} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Racket</label>
                        <input type="text" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" value={editingJob.racket || ''} onChange={e => setEditingJob({...editingJob, racket: e.target.value})} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Snaar (Lengte)</label>
                        <input type="text" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" value={editingJob.string || ''} onChange={e => setEditingJob({...editingJob, string: e.target.value})} />
                    </div>
                    
                    <div className="flex items-center mb-1 mt-2">
                        <input type="checkbox" id="editHybridToggle" checked={editingJob.isHybrid || false} onChange={e => setEditingJob({...editingJob, isHybrid: e.target.checked})} className="w-4 h-4 text-indigo-600 rounded" />
                        <label htmlFor="editHybridToggle" className="ml-2 text-xs font-bold text-slate-600 dark:text-slate-400">Hybrid Setup</label>
                    </div>
                    
                    {editingJob.isHybrid && (
                        <div>
                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Breedte Snaar (Cross)</label>
                            <input type="text" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" value={editingJob.crossString || ''} onChange={e => setEditingJob({...editingJob, crossString: e.target.value})} />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Kg Lengte (Main)</label>
                            <input type="number" step="0.5" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" value={editingJob.tensionMain || ''} onChange={e => setEditingJob({...editingJob, tensionMain: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Kg Breedte (Cross)</label>
                            <input type="number" step="0.5" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" value={editingJob.tensionCross || ''} onChange={e => setEditingJob({...editingJob, tensionCross: e.target.value})} />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Prijs (€)</label>
                        <input type="number" step="0.5" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" value={editingJob.price || ''} onChange={e => setEditingJob({...editingJob, price: e.target.value})} />
                    </div>
                    
                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Deadline</label>
                        <div className="flex gap-2">
                            <input type="date" className="flex-1 p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" value={editingJob.deadline || ''} onChange={e => setEditingJob({...editingJob, deadline: e.target.value})} />
                            <input type="time" className="w-24 p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" value={editingJob.deadlineTime || ''} onChange={e => setEditingJob({...editingJob, deadlineTime: e.target.value})} />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Bespanner</label>
                        <select className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900 dark:text-white" value={editingJob.stringer || ''} onChange={e => setEditingJob({...editingJob, stringer: e.target.value})}>
                            <option value="">-- Geen --</option>
                            {stringers.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                    </div>

                    <button type="submit" className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition">Opslaan</button>
                </form>
            </div>
        </div>
    );
}